import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useAuth as useClerkAuth } from "@clerk/react";
import api from "@/lib/api";
import { useAuth } from "./AuthContext";

interface Tenant {
    id: string;
    name: string;
    subdomain: string;
    plan: "starter" | "professional" | "enterprise" | "expired";
    modules_enabled: string[];
    is_active: boolean;
    onboarding_completed_at?: string | null;
}

interface TenantContextType {
    tenant: Tenant | null;
    isLoading: boolean;
    error: boolean;
    errorMessage: string | null;
    hasModule: (module: string) => boolean;
    setDevTenant: (subdomain: string) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated } = useAuth();
    const { isSignedIn, isLoaded } = useClerkAuth();
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!isLoaded) return;

        if (!isAuthenticated || !isSignedIn) {
            setTenant(null);
            setIsLoading(false);
            setError(false);
            setErrorMessage(null);
            return;
        }

        setIsLoading(true);
        const fetchTenant = async () => {
            try {
                // After an invite claim, the tenant_id is passed as a query param
                // so we can resolve directly by ID without relying on the
                // clerk_user_id fallback chain which may not have propagated yet.
                const params = new URLSearchParams(window.location.search);
                const claimedTenant = params.get("claimed_tenant");

                let endpoint = "/tenant/me";
                if (claimedTenant) {
                    endpoint = `/tenant/me?claimed_tenant=${claimedTenant}`;
                }

                const { data } = await api.get<Tenant>(endpoint);
                setTenant(data);
                setError(false);
                setErrorMessage(null);

                // Only clean the URL after a successful fetch so a failed request
                // can be retried with the same claimed_tenant hint still in the URL.
                if (claimedTenant) {
                    params.delete("claimed_tenant");
                    const cleanUrl = params.toString()
                        ? `${window.location.pathname}?${params}`
                        : window.location.pathname;
                    window.history.replaceState({}, "", cleanUrl);
                }
            } catch (err: any) {
                console.error("Failed to fetch tenant", err);
                setTenant(null);
                // 404 means no tenant exists (genuine new user) — not an error
                // Other status codes are actual errors
                const status = err?.response?.status;
                const msg = err?.response?.data?.error || null;
                setError(status !== 404);
                setErrorMessage(msg);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTenant();
    }, [isAuthenticated, isSignedIn, isLoaded]);

    const hasModule = (module: string): boolean => {
        return tenant?.modules_enabled.includes(module) ?? false;
    };

    const setDevTenant = (subdomain: string) => {
        localStorage.setItem("dev_tenant", subdomain);
        window.location.reload();
    };

    const value: TenantContextType = { tenant, isLoading, error, errorMessage, hasModule, setDevTenant };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant() {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error("useTenant must be used within a TenantProvider");
    }
    return context;
}